import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { FaFlag } from "react-icons/fa"
import type { EmployeeOnboardingDetailsDto } from "@/modules/employee/employee/dto/employeeDetails"
import {
  employeeProfileSchema,
  type EmployeePersonalDetailsFormData,
} from "@/modules/employee/employee/schemas/employeeFormSchemas"
import { IdentificationType } from "../../employee/schemas/identityType"
import { parseRsaIdDob } from "@/modules/employee/employee/utils/rsaId"

interface EmployeePersonalDetailsFormProps {
  formData: EmployeeOnboardingDetailsDto
  onUpdate: (data: Partial<EmployeeOnboardingDetailsDto>) => void
  isReadOnly?: boolean
}

export function ProfileDetailsForm({
  formData,
  onUpdate,
  isReadOnly = false,
}: EmployeePersonalDetailsFormProps) {
  const form = useForm<EmployeePersonalDetailsFormData>({
    resolver: zodResolver(employeeProfileSchema),
    defaultValues: {
      firstName: formData.firstName,
      secondName: formData.secondName,
      lastName: formData.lastName,
      idType: formData.identityType,
      idNumber: formData.identityNumber,
      gender: formData.gender,
      taxNumber: formData.taxNumber,
      raceGroup: formData.raceGroup,
      maritalStatus: formData.maritalStatus,
      dateOfBirth: formData.dateOfBirth,
      phone: formData.phone,
      email: formData.email,
      position: formData.position,
      emergencyContactName: formData.emergencyContactName,
      emergencyContactPhone: formData.emergencyContactPhone,
    },
    mode: "onChange",
  })

  const { watch, reset, getValues, trigger, setValue } = form

  // Sync form values back to parent
  useEffect(() => {
    const subscription = watch((values) => {
      onUpdate({
        firstName: values.firstName,
        secondName: values.secondName,
        lastName: values.lastName,
        gender: values.gender,
        dateOfBirth: values.dateOfBirth,
        phone: values.phone,
        email: values.email,
        position: values.position,
        identityType: values.idType,
        identityNumber: values.idNumber,
        emergencyContactName: values.emergencyContactName,
        emergencyContactPhone: values.emergencyContactPhone,
        taxNumber: values.taxNumber,
        raceGroup: values.raceGroup,
        maritalStatus: values.maritalStatus,
      })
    })
    return () => subscription.unsubscribe()
  }, [watch, onUpdate])

  // Sync external formData into form when it changes from outside (e.g. API load).
  // Skip if current form values already match — means the change originated from this form.
  // Coerce nullish → "" on both sides: the API returns null for optional string fields
  // while RHF stores "", and a raw !== comparison would loop reset → watch → onUpdate.
  useEffect(() => {
    const s = (v: unknown) => (v ?? "") as string
    const current = getValues()
    const hasExternalChange =
      s(current.firstName) !== s(formData.firstName) ||
      s(current.secondName) !== s(formData.secondName) ||
      s(current.lastName) !== s(formData.lastName) ||
      s(current.gender) !== s(formData.gender) ||
      s(current.dateOfBirth) !== s(formData.dateOfBirth) ||
      s(current.phone) !== s(formData.phone) ||
      s(current.email) !== s(formData.email) ||
      s(current.position) !== s(formData.position) ||
      s(current.idType) !== s(formData.identityType) ||
      s(current.idNumber) !== s(formData.identityNumber) ||
      s(current.emergencyContactName) !== s(formData.emergencyContactName) ||
      s(current.emergencyContactPhone) !== s(formData.emergencyContactPhone) ||
      s(current.taxNumber) !== s(formData.taxNumber) ||
      s(current.raceGroup) !== s(formData.raceGroup) ||
      s(current.maritalStatus) !== s(formData.maritalStatus)

    if (!hasExternalChange) return

    reset({
      firstName: s(formData.firstName),
      secondName: s(formData.secondName),
      lastName: s(formData.lastName),
      gender: s(formData.gender),
      dateOfBirth: s(formData.dateOfBirth),
      phone: s(formData.phone),
      email: s(formData.email),
      position: s(formData.position),
      idType: s(formData.identityType),
      idNumber: s(formData.identityNumber),
      emergencyContactName: s(formData.emergencyContactName),
      emergencyContactPhone: s(formData.emergencyContactPhone),
      taxNumber: s(formData.taxNumber),
      raceGroup: s(formData.raceGroup),
      maritalStatus: s(formData.maritalStatus),
    })
    trigger()
  }, [formData, reset, getValues, trigger])

  // Auto-populate DOB from RSA ID first 6 digits when the user types an ID number.
  // Skips on external data resets (name === undefined) to avoid overwriting API-loaded data.
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (!name) return
      if (name !== "idType" && name !== "idNumber") return
      if (values.idType !== IdentificationType.NationalId) return
      const idNumber = values.idNumber ?? ""
      if (idNumber.length < 6) return
      const dob = parseRsaIdDob(idNumber)
      if (dob !== null) {
        setValue("dateOfBirth", dob, { shouldValidate: true })
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setValue])

  const sectionFlag = formData.fieldFlags?.find(
    (f) => f.fieldName === "personal" && !f.resolvedAt,
  )
  const isSectionFlagged = !!sectionFlag

  const isFieldDisabled = isReadOnly && !isSectionFlagged

  return (
    <Form {...form}>
      <form className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">Profile Details</h2>

        {isSectionFlagged && (
          <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
            <FaFlag className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium mb-1">
                This section needs to be updated:
              </p>
              <p className="text-orange-700">{sectionFlag.note}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="secondName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Second Name</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="idType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Identification Type *</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isFieldDisabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select identification type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-card">
                    <SelectItem
                      value={IdentificationType.NationalId}
                      className="hover:bg-slate-200"
                    >
                      RSA ID
                    </SelectItem>
                    <SelectItem
                      value={IdentificationType.Passport}
                      className="hover:bg-slate-200"
                    >
                      Passport
                    </SelectItem>
                    <SelectItem
                      value={IdentificationType.Other}
                      className="hover:bg-slate-200"
                    >
                      Other
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="idNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Identification Number *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode={
                      watch("idType") === IdentificationType.NationalId
                        ? "numeric"
                        : "text"
                    }
                    onChange={(e) => {
                      const val =
                        watch("idType") === IdentificationType.NationalId
                          ? e.target.value.replace(/\D/g, "")
                          : e.target.value.replace(/\s/g, "")
                      field.onChange(val)
                    }}
                    disabled={isFieldDisabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gender *</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isFieldDisabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-card">
                    <SelectItem value="Male" className="hover:bg-slate-200">
                      Male
                    </SelectItem>
                    <SelectItem value="Female" className="hover:bg-slate-200">
                      Female
                    </SelectItem>
                    <SelectItem value="Other" className="hover:bg-slate-200">
                      Other
                    </SelectItem>
                    <SelectItem
                      value="Prefer not to say"
                      className="hover:bg-slate-200"
                    >
                      Prefer not to say
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dateOfBirth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date of Birth *</FormLabel>
                <FormControl>
                  <Input type="date" {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number *</FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    {...field}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) =>
                      field.onChange(e.target.value.replace(/\D/g, ""))
                    }
                    disabled={isFieldDisabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input type="email" {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="position"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Position *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="taxNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tax Number</FormLabel>
              <FormControl>
                <Input {...field} disabled={isFieldDisabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="raceGroup"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Race Group *</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isFieldDisabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select race group" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card">
                  <SelectItem value="A" className="hover:bg-slate-200">
                    African
                  </SelectItem>
                  <SelectItem value="C" className="hover:bg-slate-200">
                    Coloured
                  </SelectItem>
                  <SelectItem value="I" className="hover:bg-slate-200">
                    Indian
                  </SelectItem>
                  <SelectItem value="W" className="hover:bg-slate-200">
                    White
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="maritalStatus"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Marital Status *</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isFieldDisabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select marital status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card">
                  <SelectItem value="Single" className="hover:bg-slate-200">
                    Single
                  </SelectItem>
                  <SelectItem value="Married" className="hover:bg-slate-200">
                    Married
                  </SelectItem>
                  <SelectItem value="Divorced" className="hover:bg-slate-200">
                    Divorced
                  </SelectItem>
                  <SelectItem value="Widowed" className="hover:bg-slate-200">
                    Widowed
                  </SelectItem>
                  <SelectItem
                    value="PermanentlySeparated"
                    className="hover:bg-slate-200"
                  >
                    Permanently Separated
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {(() => {
          const emergencyFlag = formData.fieldFlags?.find(
            (f) => f.fieldName === "emergency" && !f.resolvedAt,
          )
          const isEmergencyFlagged = !!emergencyFlag
          const isEmergencyDisabled = isReadOnly && !isEmergencyFlagged

          return (
            <>
              <h3 className="text-base font-semibold text-foreground mt-6 mb-3">
                Emergency Contact
              </h3>

              {isEmergencyFlagged && (
                <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
                  <FaFlag className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium mb-1">
                      This section needs to be updated:
                    </p>
                    <p className="text-orange-700">{emergencyFlag.note}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="emergencyContactName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emergency Contact Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isEmergencyDisabled}
                          placeholder="Full name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="emergencyContactPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emergency Contact Phone *</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          {...field}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/\D/g, ""))
                          }
                          disabled={isEmergencyDisabled}
                          placeholder="Phone number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )
        })()}
      </form>
    </Form>
  )
}